// @ts-nocheck
import { lazy, Suspense, useContext } from 'react'
import { Outlet } from 'react-router'
import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'

import Header from 'js/layout/Header/Header'
import { START_OF_CONTENT_ELEMENT_ID } from 'js/lib/constants'
import { globalStateContext } from 'js/lib/GlobalState'
import { useSiteStore } from 'js/lib/stores'
import { pageViewSiteFragment } from 'js/page/Item'
import PageRow from 'js/page/Page/PageRow'
import { rowsViewFragment } from 'js/page/Page/rowsFragment'

import AdminOverlay from './AdminOverlay'
import CookieNotification from './CookieNotification'
import styles from './Layout.module.css'

const ChatOverlay = lazy(
    () =>
        import(
            /* webpackChunkName: "chat-overlay" */ './ChatOverlay/ChatOverlay'
        ),
)

const Layout = () => {
    const cookieConsent = useSiteStore(({ site }) => site.cookieConsent)
    const { globalState, setGlobalState } = useContext(globalStateContext)

    const { data = {}, loading } = useQuery<any>(GET_TOP_MENU_AND_FOOTER)

    if (loading) {
        return null
    }

    const { site, viewer, siteSettings } = data
    const { chatEnabled, footerPageEnabled, footerRows } = site || {}

    const toggleEditMode = () => {
        setGlobalState((newState) => {
            newState.editMode = !globalState.editMode
        })
    }

    let cookieNotification
    if (cookieConsent && viewer && !viewer.loggedIn) {
        cookieNotification = <CookieNotification />
    }

    const hasFooter = footerPageEnabled && footerRows?.length > 0

    return (
        <div className={styles.wrapper}>
            <Header site={site} siteSettings={siteSettings} viewer={viewer} />
            <main
                className={styles.layoutMain}
                id={START_OF_CONTENT_ELEMENT_ID}
                tabIndex="-1"
            >
                <AdminOverlay
                    viewer={viewer}
                    editMode={globalState.editMode}
                    toggleEditMode={toggleEditMode}
                    positionLeft={!!globalState.showChatOverlay}
                />
                {chatEnabled && viewer?.loggedIn && (
                    <Suspense>
                        <ChatOverlay />
                    </Suspense>
                )}
                {cookieNotification}
                <Outlet />
            </main>
            {hasFooter && (
                <footer>
                    {footerRows.map((row, idx) => (
                        <PageRow
                            key={`footer-rows-${idx}`}
                            guid={site.guid}
                            site={site}
                            rows={footerRows}
                            row={row}
                            rowIndex={idx}
                        />
                    ))}
                </footer>
            )}
        </div>
    )
}

export const GET_TOP_MENU_AND_FOOTER = gql`
    query TopMenu {
        site {
            guid
            logo
            logoAlt
            icon
            iconAlt
            showIcon
            showPreheader
            menuState
            menu {
                label
                localLabel
                link
                children {
                    label
                    localLabel
                    link
                    children {
                        label
                        localLabel
                        link
                    }
                }
            }
            blockedUserIntroMessage
            footer {
                title
                link
            }
            chatEnabled
            contentModerationEnabled
            requireContentModerationFor
            commentModerationEnabled
            requireCommentModerationFor
            footerPageEnabled
            footerRows {
                ${rowsViewFragment}
            }
            ...PageViewSiteFragment
        }
        viewer {
            guid
            loggedIn
            isAdmin
            isSubEditor
            isBanned
            isAutoBan
            banReason
            updateAccountUrl
            showSuspendAdminPrivilegesToggle
            isSuspendAdminPrivileges
            user {
                guid
                username
                name
                icon
                isChatUsersNotificationPushEnabled
                isChatGroupNotificationPushEnabled
                isOnline
                onlineStatus
                onlineStatusUpdatedAt
            }
            canAccessAdmin(permission: accessAdmin)
        }
    }
    ${pageViewSiteFragment}
`

export default Layout
